import { mergeAttributes, Node, nodeInputRule } from '@tiptap/core';
import Suggestion, { SuggestionOptions } from '@tiptap/suggestion';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { PluginKey } from 'prosemirror-state';

import { EMOJI_EMOTICONS, EMOJIS_HEXMAP, EMOTICON_REGEX } from '@models/utils/emoji-utils';

export type EmojiOptions = {
    HTMLAttributes: Record<string, any>,
    renderLabel: (props: {
        options: EmojiOptions,
        node: ProseMirrorNode,
    }) => string | HTMLElement,
    suggestion: Omit<SuggestionOptions, 'editor'>,
}

export const EmojiPluginKey = new PluginKey('Emoji')

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        emoji: {
            insertEmoji: (emoji: string) => ReturnType,
            insertEmoticon: (txt: string) => ReturnType
        }
    }
}

export const VzEmoji = Node.create<EmojiOptions>({
    name: 'emoji',
    addOptions() {
        return {
            HTMLAttributes: {},
            renderLabel({ node }) {
                return EMOJIS_HEXMAP[node.attrs.hexcode]?.unicode || '⍰';
            },
            suggestion: {
                char: ':',
                pluginKey: EmojiPluginKey,
                decorationClass: 'vz-emoji',
                prefixSpace: true,
                command: ({ editor, range, props }) => {
                    // increase range.to by one when the next node is of type "text"
                    // and starts with a space character
                    const nodeAfter = editor.view.state.selection.$to.nodeAfter;
                    const overrideSpace = nodeAfter?.text?.startsWith(' ');
                    if (overrideSpace) {
                        range.to += 1;
                    }
                    editor.chain().focus().insertContentAt(range, [{ type: this.name, attrs: props }]).run();
                    window.getSelection()?.collapseToEnd();
                },
                allow: ({ state, range }) => {
                    const $from = state.doc.resolve(range.from);
                    const type = state.schema.nodes[this.name];
                    const allow = !!$from.parent.type.contentMatch.matchType(type) && (range.to - range.from) > 2;
                    return allow;
                },
            },
        }
    },
    group: 'inline',
    inline: true,
    selectable: false,
    atom: true,

    addAttributes() {
        return {
            hexcode: {
                default: null,
                parseHTML: element => element.getAttribute('data-hexcode'),
                renderHTML: attributes => attributes.hexcode ? { 'data-hexcode': attributes.hexcode } : {}
            },
        };
    },

    parseHTML() {
        return [{ tag: `span[data-type="${this.name}"]` }];
    },

    renderHTML({ node, HTMLAttributes }) {
        return [
            'span',
            mergeAttributes({ 'data-type': this.name }, this.options.HTMLAttributes, HTMLAttributes),
            this.options.renderLabel({ options: this.options, node })
        ];
    },

    addCommands() {
        return {
            insertEmoji: (hexcode) => ({ tr, dispatch }) => {
                const node = this.type.create({ hexcode });
                if (dispatch) {
                    tr.replaceRangeWith(tr.selection.from, tr.selection.to, node);
                }
                return true;
            },
            insertEmoticon: (emoticon) => ({ tr, dispatch }) => {
                if (!EMOJI_EMOTICONS[emoticon]) {
                    return false;
                }
                const node = this.type.create({ hexcode: EMOJI_EMOTICONS[emoticon] });
                if (dispatch) {
                    tr.replaceRangeWith(tr.selection.from, tr.selection.to, node);
                }
                return true;
            },
        }
    },

    addKeyboardShortcuts() {
        return {
            Backspace: () => this.editor.commands.command(({ tr, state }) => {
                let isEmoji = false
                const { selection } = state
                const { empty, anchor } = selection

                if (!empty) {
                    return false;
                }

                state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
                    if (node.type.name === this.name) {
                        isEmoji = true;
                        tr.insertText(this.options.suggestion.char || '', pos, pos + node.nodeSize);
                        return false;
                    }
                    return;
                });

                return isEmoji;
            }),
        };
    },

    addProseMirrorPlugins() {
        return [
            Suggestion({
                editor: this.editor,
                ...this.options.suggestion,
            }),
        ];
    },

    addInputRules() {
        return [
            nodeInputRule({
                find: EMOTICON_REGEX,
                type: this.type,
                getAttributes: match => {
                    return EMOJI_EMOTICONS[match[1]] ? { hexcode: EMOJI_EMOTICONS[match[1]] } : {};
                }
            }),
        ]
    },

});
